import React from "react";
import { useLanguage } from "@/lib/lang";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import { IMG } from "@/lib/assets";
import { PORTRAITS } from "@/lib/portraits";

const ASSET = (name) =>
  `https://customer-assets.emergentagent.com/job_peony-redesign/artifacts/${name}`;

const PressKitPage = () => {
  const { lang } = useLanguage();
  useDocumentMeta({
    title: "Press · patrice.studio",
    description: "Press-Kit von patrice.studio — Pressetext, Pressefotos, Logos.",
  });

  const downloads = [
    {
      label: "Pressefoto · Portrait",
      file: PORTRAITS.aboutHero.src,
      kind: "JPG · 3:4",
    },
    {
      label: "Pressefoto · Studio",
      file: IMG.studioHeader,
      kind: "JPG · 16:9",
    },
    {
      label: "Pressefoto · Atelier",
      file: IMG.peonyAtelier,
      kind: "JPG",
    },
    {
      label: "Logo · patrice.studio",
      file: ASSET("owx1dp7z_6E703CF7-E51A-4C40-AFF8-ABEE3CEDB1E7.PNG"),
      kind: "PNG · square",
    },
    {
      label: "Logo · Sichtbar Gut Bewertet",
      file: ASSET("7zzmjsnb_Geil3.png"),
      kind: "PNG · square",
    },
  ];

  return (
    <section className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-20 pb-32 overflow-hidden">
      <PeonyMark size={680} opacity={0.05} className="absolute -right-40 -top-32 pointer-events-none" />

      <div className="grid grid-cols-12 gap-6 lg:gap-12">
        <div className="col-span-12 lg:col-span-8">
          <Reveal>
            <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
              Press · 2026
            </p>
            <h1 className="font-display text-[clamp(2.6rem,7vw,6rem)] leading-[0.96] mt-8 tracking-tight">
              {lang === "de" ? (
                <>
                  <span className="italic-serif text-atmosphere">Für die,</span>
                  <br />
                  die schreiben.
                </>
              ) : (
                <>
                  <span className="italic-serif text-atmosphere">For those</span>
                  <br />
                  who write.
                </>
              )}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="body-lux mt-12 max-w-2xl">
              {lang === "de"
                ? "Alles, was du für einen Beitrag über patrice.studio oder Sichtbar Gut Bewertet brauchst — Pressetext, Fotos, Logos. Anfragen bitte direkt an hallo@patrice.studio. Ich antworte persönlich."
                : "Everything you need for an article about patrice.studio or Sichtbar Gut Bewertet — press text, photos, logos. Requests directly to hallo@patrice.studio. I reply personally."}
            </p>
          </Reveal>
        </div>
      </div>

      {/* Pressetext */}
      <div className="mt-24 grid grid-cols-12 gap-6 lg:gap-12">
        <Reveal className="col-span-12 lg:col-span-4">
          <p className="eyebrow">— Bio</p>
        </Reveal>
        <Reveal delay={120} className="col-span-12 lg:col-span-8 space-y-6 text-depth/90 font-light text-base md:text-[1.0625rem] leading-relaxed">
          <p>
            <strong>Patrice Schreiter</strong> ist Brand- und Reputation-Strategin aus
            Limbach-Oberfrohna. Mit patrice.studio betreibt sie ein digitales Atelier, das
            mehrere Disziplinen unter einer Marke vereint: Reputation Management, Webdesign,
            Profilanalysen, sowie persönliche Erlebnisräume zu Fotografie, Kunst, Musik und
            Atelier.
          </p>
          <p>
            Ihr operatives Hauptprojekt <strong>Sichtbar Gut Bewertet</strong> hilft
            Unternehmer:innen im DACH-Raum dabei, Online-Reputation zu strukturieren —
            Bewertungen, die geschäftsschädigend sind, prüfen und gegebenenfalls löschen zu
            lassen, das Profil sichtbarer zu machen, und gesperrte Social-Media-Accounts
            wiederherzustellen.
          </p>
          <p>
            Patrice arbeitet ortsunabhängig, persönlich und mit der Disziplin einer
            ehemaligen Personalleiterin und Storemanagerin — zwei Berufe, die sie verlassen
            hat, um nicht mehr „immer höher, schneller, weiter" zu funktionieren, sondern
            etwas zu bauen, das sich wie sie selbst anfühlt.
          </p>
        </Reveal>
      </div>

      {/* Quick facts */}
      <div className="mt-20 grid grid-cols-12 gap-6 lg:gap-12">
        <Reveal className="col-span-12 lg:col-span-4">
          <p className="eyebrow">— Quick Facts</p>
        </Reveal>
        <Reveal delay={120} className="col-span-12 lg:col-span-8">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 text-depth font-light text-base">
            {[
              ["Marke", "patrice.studio"],
              ["Schwester-Marke", "Sichtbar Gut Bewertet"],
              ["Gegründet", "2026"],
              ["Standort", "Limbach-Oberfrohna · Sachsen"],
              ["Tätigkeitsgebiet", "Deutschland · Österreich · Schweiz"],
              ["Disziplinen", "Reputation · Webdesign · Profilanalyse"],
              ["Sprachen", "Deutsch · English"],
              ["Kontakt", "hallo@patrice.studio"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex flex-col gap-1 border-b border-warmth/30 pb-4"
              >
                <dt className="font-mono text-[0.58rem] tracking-[0.32em] uppercase text-atmosphere">
                  {k}
                </dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>

      {/* Downloads */}
      <div className="mt-24 grid grid-cols-12 gap-6 lg:gap-12">
        <Reveal className="col-span-12 lg:col-span-4">
          <p className="eyebrow">— Downloads</p>
          <p className="mt-6 font-light text-depth/80 text-sm leading-relaxed max-w-sm">
            {lang === "de"
              ? "Rechtsklick → „Bild speichern unter…" + " oder anklicken & speichern. Verwendung nur im redaktionellen Kontext."
              : "Right-click → 'Save image as…' or click & download. Editorial use only."}
          </p>
        </Reveal>
        <Reveal delay={120} className="col-span-12 lg:col-span-8">
          <ul className="space-y-4">
            {downloads.map((d) => (
              <li key={d.label}>
                <a
                  href={d.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid={`press-download-${d.label}`}
                  className="group flex items-baseline justify-between gap-6 py-5 border-b border-warmth/30 hover:border-depth transition-colors"
                >
                  <span className="font-display text-lg md:text-xl text-depth group-hover:text-signal transition-colors">
                    {d.label}
                  </span>
                  <span className="flex items-center gap-4 font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
                    {d.kind}
                    <span aria-hidden className="text-depth group-hover:text-signal transition-colors">
                      ↗
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};

export default PressKitPage;
